<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import BotsAPI from 'dashboard/api/bots';
import ThemeToggleButton from 'dashboard/components-next/sidebar/ThemeToggleButton.vue';
import { TEMPLATES, TEMPLATE_CATEGORIES, CHANNELS } from '../templates.js';

const emit = defineEmits(['close', 'created']);

const store = useStore();
const activeCategory = ref('all');
const selectedChannels = ref(['whatsapp', 'instagram']);
const selectedBranchIds = ref([]); // [] = global, todas las sucursales
const submitting = ref(false);
const error = ref(null);

const availableBranches = computed(() => store.getters['branches/getBranches'] || []);
function toggleBranch(id) {
  const idx = selectedBranchIds.value.indexOf(id);
  if (idx >= 0) selectedBranchIds.value.splice(idx, 1);
  else selectedBranchIds.value.push(id);
}

onMounted(() => {
  store.dispatch('branches/get');
});

const filteredTemplates = computed(() => {
  let list = TEMPLATES;
  if (activeCategory.value !== 'all') {
    list = list.filter(t => t.category === activeCategory.value);
  }
  if (selectedChannels.value.length) {
    list = list.filter(t => t.channels.some(c => selectedChannels.value.includes(c)));
  }
  return list;
});

function toggleAllChannels(ev) {
  selectedChannels.value = ev.target.checked ? CHANNELS.map(c => c.key) : [];
}

function toggleChannel(key) {
  const idx = selectedChannels.value.indexOf(key);
  if (idx >= 0) selectedChannels.value.splice(idx, 1);
  else selectedChannels.value.push(key);
}

async function pickTemplate(tpl) {
  if (submitting.value) return;
  submitting.value = true;
  error.value = null;
  try {
    const { data } = await BotsAPI.create({
      bot: {
        name: tpl.name,
        category: tpl.category,
        channels: selectedChannels.value.filter(c => tpl.channels.includes(c)),
        scope_branch_ids: selectedBranchIds.value,
      },
      template_key: tpl.key,
    });
    emit('created', data);
  } catch (e) {
    error.value = e.response?.data?.message || e.message;
  } finally {
    submitting.value = false;
  }
}

async function startFromScratch() {
  if (submitting.value) return;
  submitting.value = true;
  error.value = null;
  try {
    // Sin prompt — el backend asigna nombre auto "SALESBOT #N" cuando el placeholder coincide.
    const { data } = await BotsAPI.create({
      bot: {
        name: '__autoname__',
        category: 'custom',
        channels: selectedChannels.value,
        scope_branch_ids: selectedBranchIds.value,
      },
    });
    emit('created', data);
  } catch (e) {
    error.value = e.response?.data?.message || e.message;
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="fixed inset-0 z-50 bg-n-background flex flex-col">
    <!-- header con toggle tema + botón cerrar -->
    <div class="flex items-center justify-between px-6 py-3 border-b border-n-weak">
      <h2 class="text-base font-semibold text-n-slate-12">Crear o importar un nuevo bot</h2>
      <div class="flex items-center gap-3">
        <ThemeToggleButton />
        <button class="text-n-slate-11 hover:text-n-slate-12 text-2xl leading-none" @click="emit('close')">×</button>
      </div>
    </div>

    <div class="grid flex-1 min-h-0" style="grid-template-columns: 300px 1fr;">
      <!-- Sidebar -->
      <aside class="border-r border-n-weak p-5 bg-n-alpha-black1 overflow-y-auto">
        <h3 class="text-base font-semibold text-n-slate-12">Crear un bot</h3>
        <button
          class="w-full mt-4 px-3 py-2.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 font-medium disabled:opacity-50"
          :disabled="submitting"
          @click="startFromScratch"
        >
          Comenzar desde cero
        </button>

        <div class="mt-7 text-[11px] uppercase tracking-wider text-n-slate-11 font-semibold">Canales</div>
        <ul class="mt-2 space-y-2 text-sm text-n-slate-12">
          <li class="flex items-center gap-2">
            <input
              type="checkbox"
              class="w-3.5 h-3.5"
              :checked="selectedChannels.length === CHANNELS.length"
              @change="toggleAllChannels"
            >
            Seleccionar todos
          </li>
          <li v-for="c in CHANNELS" :key="c.key" class="flex items-center gap-2">
            <input
              type="checkbox"
              class="w-3.5 h-3.5"
              :checked="selectedChannels.includes(c.key)"
              @change="toggleChannel(c.key)"
            >
            <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: c.color }" />
            {{ c.label }}
          </li>
        </ul>

        <div v-if="availableBranches.length > 0" class="mt-7">
          <div class="text-[11px] uppercase tracking-wider text-n-slate-11 font-semibold">Sucursales</div>
          <p class="text-[10px] text-n-slate-11 mt-1 mb-2">
            Sin selección = aplica a TODAS
          </p>
          <ul class="space-y-2 text-sm text-n-slate-12">
            <li v-for="b in availableBranches" :key="b.id" class="flex items-center gap-2">
              <input
                type="checkbox"
                class="w-3.5 h-3.5"
                :checked="selectedBranchIds.includes(b.id)"
                @change="toggleBranch(b.id)"
              >
              <span class="text-[10px] font-bold w-7 text-center px-1.5 py-0.5 rounded" style="background: rgba(251,191,36,0.15); color: #d97706;">{{ b.code }}</span>
              {{ b.name }}
            </li>
          </ul>
        </div>
      </aside>

      <!-- Contenido -->
      <div class="overflow-y-auto p-6">
        <!-- Tabs -->
        <div class="flex flex-wrap gap-2 mb-4">
          <button
            v-for="cat in TEMPLATE_CATEGORIES"
            :key="cat.key"
            class="px-4 py-1.5 text-xs rounded-full transition"
            :class="activeCategory === cat.key
              ? 'bg-blue-600 text-white'
              : 'bg-n-alpha-black2 text-n-slate-12 hover:bg-n-alpha-black3'"
            @click="activeCategory = cat.key"
          >
            {{ cat.label }}
          </button>
        </div>

        <!-- Banner IA -->
        <div class="rounded-lg bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 px-5 py-4 mb-5 flex items-center gap-3">
          <div class="w-11 h-11 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700 text-2xl">⚡</div>
          <div class="flex-1">
            <div class="text-sm font-semibold text-n-slate-12">Prueba el agente de IA — comunicación automatizada con clientes 24/7</div>
            <div class="text-xs text-n-slate-11 mt-0.5">Comienza con una plantilla prediseñada o crea tu propio agente desde cero.</div>
          </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{{ error }}</div>

        <!-- Grid responsive -->
        <div
          v-if="filteredTemplates.length"
          class="grid gap-4"
          style="grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));"
        >
          <button
            v-for="tpl in filteredTemplates"
            :key="tpl.key"
            class="text-left border border-n-weak rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-md cursor-pointer transition disabled:opacity-50"
            :disabled="submitting"
            @click="pickTemplate(tpl)"
          >
            <div :class="tpl.bg" class="h-28 flex items-center justify-center">
              <!-- bubble siempre blanca con texto oscuro: queda legible sobre el pastel en cualquier modo -->
              <div class="px-3 py-2 rounded-md text-xs shadow bg-white text-slate-900">{{ tpl.bubble }}</div>
            </div>
            <div class="px-3 py-2.5 text-xs text-n-slate-12">{{ tpl.name }}</div>
          </button>
        </div>

        <div v-else class="text-center text-sm text-n-slate-11 py-12">
          Ninguna plantilla coincide con los canales/categoría seleccionados.
        </div>
      </div>
    </div>
  </div>
</template>
